import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { AdminSection } from "./admin";
import {
  AdminButton,
  AdminDialog,
  Field,
  Input,
  Row,
  StatusBadge,
  TableShell,
  Textarea,
} from "@/components/admin/AdminUI";
import { MediaUpload } from "@/components/admin/MediaUpload";

export const Route = createFileRoute("/_authenticated/admin/releases")({
  component: ReleasesAdmin,
});

type Release = Database["public"]["Tables"]["releases"]["Row"];
type ReleaseInsert = Database["public"]["Tables"]["releases"]["Insert"];

const EMPTY: ReleaseInsert = {
  slug: "",
  title: "",
  type: "album",
  year: null,
  year_placeholder: "",
  statement: "",
  tracks: [],
  mood: [],
  frequency: "",
  status: "coming_soon",
  cover_url: "",
  featured: false,
  sort_order: 0,
};

const TYPES = ["album", "ep", "single", "meditation", "score"];
const STATUSES = ["coming_soon", "live", "draft", "archived"];

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/['']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function ReleasesAdmin() {
  const [rows, setRows] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Release | "new" | null>(null);
  const [songsFor, setSongsFor] = useState<Release | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [query, setQuery] = useState("");

  async function load() {
    setLoading(true);
    const { data } = await supabase.from("releases").select("*").order("sort_order").order("created_at");
    setRows(data ?? []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function remove(id: string) {
    if (!confirm("Delete this release? This cannot be undone.")) return;
    const { error } = await supabase.from("releases").delete().eq("id", id);
    if (error) alert(error.message); else load();
  }

  const filtered = rows.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      if (!r.title.toLowerCase().includes(q) && !r.slug.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <AdminSection
      title="Releases"
      description="Albums, EPs, singles, meditations, and scores in the KAfrequency archive."
      action={<AdminButton onClick={() => setEditing("new")}>+ New release</AdminButton>}
    >
      <p className="mb-6 rounded-lg border hairline bg-obsidian/40 p-4 text-xs text-muted-foreground">
        Releases and their songs edited here are the source of truth for{" "}
        <code>/music</code>, <code>/music/:slug</code>, <code>/music/:slug/song/:song</code>,
        and the home page featured section. Changes go live on next deploy.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.2em]">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search title or slug…"
          className="rounded-full border hairline bg-transparent px-4 py-1.5 text-xs normal-case tracking-normal focus:border-gold focus:outline-none"
        />
        <div className="ml-auto flex flex-wrap gap-2">
          {(["all", ...STATUSES] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`rounded-full border px-3 py-1 transition ${
                statusFilter === s ? "border-gold text-gold" : "hairline text-muted-foreground hover:border-gold hover:text-gold"
              }`}
            >
              {s.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {rows.length === 0 ? "No releases yet." : "No releases match this filter."}
        </p>
      ) : (
        <TableShell
          headers={["Order", "Title", "Type", "Year", "Status", "Featured", ""]}
          rows={filtered.map((r) => (
            <tr key={r.id} className="hover:bg-obsidian/40">
              <td className="px-4 py-3 text-muted-foreground">{r.sort_order}</td>
              <td className="px-4 py-3">
                <div className="font-serif text-base text-gold-luminous">{r.title}</div>
                <div className="text-xs text-muted-foreground">/{r.slug}</div>
              </td>
              <td className="px-4 py-3 text-muted-foreground">{r.type}</td>
              <td className="px-4 py-3 text-muted-foreground">{r.year ?? r.year_placeholder}</td>
              <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-3 text-muted-foreground">{r.featured ? "★" : ""}</td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-3">
                  <a
                    href={`/music/${r.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-gold"
                  >
                    View ↗
                  </a>
                  <AdminButton variant="ghost" onClick={() => setSongsFor(r)}>Songs</AdminButton>
                  <AdminButton variant="ghost" onClick={() => setEditing(r)}>Edit</AdminButton>
                  <AdminButton variant="danger" onClick={() => remove(r.id)}>Delete</AdminButton>
                </div>
              </td>
            </tr>
          ))}
        />
      )}

      {editing && (
        <ReleaseForm
          initial={editing === "new" ? EMPTY : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {songsFor && (
        <SongsManager release={songsFor} onClose={() => setSongsFor(null)} />
      )}
    </AdminSection>
  );
}

function ReleaseForm({ initial, onClose, onSaved }: {
  initial: Release | ReleaseInsert;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !("id" in initial);
  const [f, setF] = useState({
    slug: initial.slug ?? "",
    title: initial.title ?? "",
    type: initial.type ?? "album",
    year: initial.year?.toString() ?? "",
    year_placeholder: initial.year_placeholder ?? "",
    statement: initial.statement ?? "",
    tracks: (initial.tracks ?? []).join("\n"),
    mood: (initial.mood ?? []).join(", "),
    frequency: initial.frequency ?? "",
    status: initial.status ?? "coming_soon",
    cover_url: initial.cover_url ?? "",
    featured: initial.featured ?? false,
    sort_order: initial.sort_order?.toString() ?? "0",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    const payload: ReleaseInsert = {
      slug: f.slug.trim(),
      title: f.title.trim(),
      type: f.type,
      year: f.year ? parseInt(f.year, 10) : null,
      year_placeholder: f.year_placeholder.trim(),
      statement: f.statement,
      tracks: f.tracks.split("\n").map((s) => s.trim()).filter(Boolean),
      mood: f.mood.split(",").map((s) => s.trim()).filter(Boolean),
      frequency: f.frequency.trim() || null,
      status: f.status,
      cover_url: f.cover_url.trim() || null,
      featured: f.featured,
      sort_order: parseInt(f.sort_order, 10) || 0,
    };
    const q = isNew
      ? supabase.from("releases").insert(payload)
      : supabase.from("releases").update(payload).eq("id", (initial as Release).id);
    const { error } = await q;
    setBusy(false);
    if (error) setError(error.message); else onSaved();
  }

  return (
    <AdminDialog open onClose={onClose} title={isNew ? "New release" : `Edit · ${initial.title}`}>
      <form onSubmit={submit} className="space-y-4">
        <Row>
          <Field label="Title">
            <Input
              required
              value={f.title}
              onChange={(e) => {
                const title = e.target.value;
                // Auto-fill slug from title while creating, until the user edits it manually.
                setF((prev) => ({
                  ...prev,
                  title,
                  slug: isNew && (prev.slug === "" || prev.slug === slugify(prev.title))
                    ? slugify(title)
                    : prev.slug,
                }));
              }}
            />
          </Field>
          <Field label="Slug" hint="Lowercase, hyphen-separated"><Input required value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} /></Field>
        </Row>
        <Row>
          <Field label="Type">
            <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm">
              {TYPES.map((t) => <option key={t} value={t} className="bg-background">{t}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm">
              {STATUSES.map((t) => <option key={t} value={t} className="bg-background">{t}</option>)}
            </select>
          </Field>
        </Row>
        <Row>
          <Field label="Year"><Input type="number" value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} /></Field>
          <Field label="Year placeholder" hint="Used when year is TBD, e.g. 'Forthcoming'"><Input value={f.year_placeholder} onChange={(e) => setF({ ...f, year_placeholder: e.target.value })} /></Field>
        </Row>
        <Field label="Statement"><Textarea rows={4} value={f.statement} onChange={(e) => setF({ ...f, statement: e.target.value })} /></Field>
        <Field label="Tracks" hint="One per line"><Textarea rows={5} value={f.tracks} onChange={(e) => setF({ ...f, tracks: e.target.value })} /></Field>
        <Row>
          <Field label="Mood" hint="Comma-separated"><Input value={f.mood} onChange={(e) => setF({ ...f, mood: e.target.value })} /></Field>
          <Field label="Frequency"><Input value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })} /></Field>
        </Row>
        <Row>
          <Field label="Cover image">
            <MediaUpload value={f.cover_url} onChange={(url) => setF({ ...f, cover_url: url })} folder="releases" />
          </Field>
          <Field label="Sort order"><Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: e.target.value })} /></Field>
        </Row>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} />
          <span>Featured on the home page</span>
        </label>

        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex justify-end gap-2 border-t hairline pt-4">
          <AdminButton type="button" variant="ghost" onClick={onClose}>Cancel</AdminButton>
          <AdminButton type="submit" disabled={busy}>{busy ? "Saving…" : isNew ? "Create" : "Save"}</AdminButton>
        </div>
      </form>
    </AdminDialog>
  );
}

type Song = Database["public"]["Tables"]["songs"]["Row"];
type SongInsert = Database["public"]["Tables"]["songs"]["Insert"];

function SongsManager({ release, onClose }: { release: Release; onClose: () => void }) {
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<SongInsert>({
    release_slug: release.slug,
    slug: "",
    title: "",
    duration: "",
    audio_url: "",
    sort_order: 0,
  });
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from("songs")
      .select("*")
      .eq("release_slug", release.slug)
      .order("sort_order")
      .order("created_at");
    setSongs((data as Song[]) ?? []);
    setLoading(false);
  }
  useEffect(() => { load(); }, [release.id]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const payload: SongInsert = {
      ...draft,
      release_slug: release.slug,
      slug: (draft.slug ?? "").trim() || slugify(draft.title ?? ""),
      title: (draft.title ?? "").trim(),
      duration: (draft.duration ?? "")?.toString().trim() || null,
      audio_url: (draft.audio_url ?? "")?.toString().trim() || null,
      sort_order: Number(draft.sort_order) || songs.length,
    };
    if (!payload.title || !payload.slug) { setError("Title and slug are required."); return; }
    const { error } = await supabase.from("songs").insert(payload);
    if (error) { setError(error.message); return; }
    setDraft({ release_slug: release.slug, slug: "", title: "", duration: "", audio_url: "", sort_order: songs.length + 1 });
    load();
  }

  async function updateField(id: string, patch: Partial<SongInsert>) {
    const { error } = await supabase.from("songs").update(patch).eq("id", id);
    if (error) alert(error.message); else load();
  }

  async function removeSong(id: string) {
    if (!confirm("Delete this song?")) return;
    const { error } = await supabase.from("songs").delete().eq("id", id);
    if (error) alert(error.message); else load();
  }

  return (
    <AdminDialog open onClose={onClose} title={`Songs · ${release.title}`}>
      <div className="space-y-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : songs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No songs yet.</p>
        ) : (
          <ul className="divide-y hairline rounded-md border hairline">
            {songs.map((s) => (
              <li key={s.id} className="grid grid-cols-[3rem_1fr_6rem_auto] items-center gap-2 px-3 py-2 text-sm">
                <input
                  type="number"
                  defaultValue={s.sort_order ?? 0}
                  onBlur={(e) => updateField(s.id, { sort_order: parseInt(e.target.value, 10) || 0 })}
                  className="rounded border hairline bg-transparent px-2 py-1 text-xs"
                />
                <div className="min-w-0">
                  <div className="truncate font-serif text-gold-luminous">{s.title}</div>
                  <div className="truncate text-xs text-muted-foreground">/{s.slug}</div>
                </div>
                <span className="text-xs text-muted-foreground">{s.duration ?? ""}</span>
                <AdminButton variant="danger" onClick={() => removeSong(s.id)}>Delete</AdminButton>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={add} className="space-y-3 border-t hairline pt-4">
          <p className="text-xs uppercase tracking-[0.2em] text-gold">Add song</p>
          <Row>
            <Field label="Title">
              <Input
                value={draft.title ?? ""}
                onChange={(e) => setDraft((d) => ({
                  ...d,
                  title: e.target.value,
                  slug: !d.slug || d.slug === slugify(d.title ?? "") ? slugify(e.target.value) : d.slug,
                }))}
              />
            </Field>
            <Field label="Slug"><Input value={draft.slug ?? ""} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} /></Field>
          </Row>
          <Row>
            <Field label="Duration" hint="e.g. 3:42"><Input value={(draft.duration ?? "") as string} onChange={(e) => setDraft({ ...draft, duration: e.target.value })} /></Field>
            <Field label="Sort order"><Input type="number" value={String(draft.sort_order ?? 0)} onChange={(e) => setDraft({ ...draft, sort_order: parseInt(e.target.value, 10) || 0 })} /></Field>
          </Row>
          <Field label="Audio URL"><Input value={(draft.audio_url ?? "") as string} onChange={(e) => setDraft({ ...draft, audio_url: e.target.value })} /></Field>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <div className="flex justify-end gap-2">
            <AdminButton type="button" variant="ghost" onClick={onClose}>Close</AdminButton>
            <AdminButton type="submit">Add song</AdminButton>
          </div>
        </form>
      </div>
    </AdminDialog>
  );
}
